/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, lastValueFrom, map, tap } from 'rxjs';

import {
	IMPORT_TICKETS_ROUTE,
	VALIDATE_TICKETS_ROUTE,
	UPLOAD_IMPORT_TICKETS_FILE_ROUTE,
	UPLOAD_IMPORT_IMAGE_ROUTE,
	START_IMPORT_IMAGE_PROCESSING_ROUTE,
	INGEST_BOARDSCAN_TICKETS_ROUTE,
	IMPORT_BOARDSCAN_TICKETS_ROUTE,
} from 'src/app/services/rest-api-configuration/rest-api-routes';
import {
	ActiveLineStrategy,
	IESIDData,
	IImportVariances,
	ITicketImportResponseMetadata,
	ITicketOverrides,
	ImportScope,
	Role,
	SwimlanePlacement,
	ImportType,
	ITPTicket,
	IBoardscanTicketMetadata,
	ITicketProcessBoardScanTicketsResponse,
} from '@touchplan/common';
import { AngularFireDatabase, AngularFireObject } from '@angular/fire/compat/database';
import { IExternalSystem, IExternalSystemList } from '../../../services/external-systems.service';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import { fancyDatabase } from '@touchplan/client-firebase';
import { TOUCHPLAN_API_CONTEXT } from 'src/app/services/rest-api-configuration/touchplan-rest-interceptor';
export interface UploadImportTicketsFileResponse {
	uploadId: string;
}

export interface UploadBSImageFileResponse {
	imageUrl: string;
	uploadedAt: string;
	pushId?: string;
}

export interface BoardScanImageProcessingRequest {
	planStartDate: string;
}

export interface ImportTicketsValidationData {
	planId: string;
	uploadId: string;
	projectId: string;
	wbsLevel?: number;
	importFileFormat: string;
	externalSystemId: string;
	defaultRoleId: string | Role;
	swimlanePlacement?: SwimlanePlacement;
	importScope: ImportScope[];
	importType: ImportType;
}

export interface ImportTicketsValidationResponse {
	esidData?: IESIDData;
	uploadId?: string;
	needsVariance?: string[];
	issuesUrl?: string;
	errorCount: number;
	warningCount?: number;
	importCount: number;
}

interface ImportTicketsRequestBase {
	importType: string;
	insertionPointY: number;
	externalSystemId: string;
	swimlanePlacement?: SwimlanePlacement;
	wbsLevel?: number;
}

export interface ExpressImportTicketsData extends ImportTicketsRequestBase {
	projectId: string;
	jobId: string;
	planId: string;
}

export interface ImportTicketsData extends ImportTicketsRequestBase {
	esidData?: IESIDData;
	variances?: IImportVariances;
	ticketOverrides?: ITicketOverrides;
	uploadId: string;
	importScope: ImportScope[];
	activeLineStrategy: ActiveLineStrategy;
}

export interface IBSApiRequestImageResult {
	imageUrl: string;
	order: number;
	uploadedAt: string;
	error?: string;
}

export interface IBSApiRequestResults {
	statusMessage: string;
	startedAt: string;
	statusMax: number;
	statusStep: number;
	completedAt: string;
	errorMessage: string;
	statusAt: string;
	images: Record<string, IBSApiRequestImageResult>;
}

@Injectable()
export class ImportTicketsService {
	private http = inject(HttpClient);
	private fireDB = inject(AngularFireDatabase);

	/**
	 * Downloads a blank sample file for ticket import.
	 * Opens a new window with the specified file.
	 */
	public downloadBlankSampleFile(): void {
		window.open('/assets/Touchplan_Ticket_Import_Template.01.xlsx', '_self');
	}

	/**
	 * Downloads a file containing errors / warnings from the ticket import process.
	 * Opens a new window with the specified file.
	 * @param fileErrorPath
	 */
	public downloadErrorsFile(fileErrorPath: string): void {
		window.open(fileErrorPath, '_self');
	}

	/**
	 * Uploads a file for ticket imports.
	 *
	 * @param file - The file containing the tickets data to be imported.
	 * @returns An Observable emitting the response of the ticket import operation.
	 *
	 */
	public uploadImportTicketsFile(file: File): Observable<UploadImportTicketsFileResponse> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<UploadImportTicketsFileResponse>(UPLOAD_IMPORT_TICKETS_FILE_ROUTE, formData, {
			context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
				forceRefresh: true,
			}),
		});
	}

	/**
	 * Validates the provided file data for import.
	 *
	 * @param importTicketsValidationData - The data required to validate the import.
	 * @returns An Observable emitting the response of the ticket import validation operation.
	 *
	 */
	public validateImport(
		importTicketsValidationData: ImportTicketsValidationData
	): Observable<ImportTicketsValidationResponse> {
		return this.http.post<ImportTicketsValidationResponse>(
			VALIDATE_TICKETS_ROUTE(importTicketsValidationData.projectId),
			importTicketsValidationData,
			{
				context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
					forceRefresh: true,
				}),
			}
		);
	}

	/**
	 * Attempts to finish the import process.
	 *
	 * @param importTicketsData - The data required to finish the import.
	 * @returns An Observable emitting the response of the ticket import operation.
	 *
	 */
	public import(importTicketsData: ImportTicketsData): Observable<ITicketImportResponseMetadata> {
		return this.http.put<ITicketImportResponseMetadata>(IMPORT_TICKETS_ROUTE, importTicketsData, {
			context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
				forceRefresh: true,
			}),
		});
	}

	/** generate a new board scan job id. Expect to hold onto this until the end of the import */
	public generateBSJobId(): string {
		const bsPath = fancyFirebaseGetters.getApiRequestsPath();
		const ref = fancyFirebaseReferencer.getRef(bsPath);
		return fancyDatabase.generateChildKey(ref);
	}

	/**
	 * Upload a board scan image
	 */
	public uploadBSImage(
		file: File,
		projectId: string,
		jobId: string,
		order: number
	): Observable<UploadBSImageFileResponse> {
		const formData = new FormData();
		formData.append('file', file);
		// .append only accepts strings, so we need to convert the order to a string
		formData.append('order', order.toString());
		return this.http.post<UploadBSImageFileResponse>(UPLOAD_IMPORT_IMAGE_ROUTE(projectId, jobId), formData, {
			context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
				forceRefresh: true,
			}),
		});
	}

	/**
	 * Kick off image processing for the job
	 * @returns A blank notification observable, success is !failure
	 */
	public startBSImageProcessing(
		projectId: string,
		jobId: string,
		request: BoardScanImageProcessingRequest
	): Observable<void> {
		return this.http.post<void>(START_IMPORT_IMAGE_PROCESSING_ROUTE(projectId, jobId), request, {
			context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
				forceRefresh: true,
			}),
		});
	}

	public processBSTickets(
		projectId: string,
		jobId: string
	): Observable<ITicketProcessBoardScanTicketsResponse> {
		console.log('ingest Boardscan called with project:', projectId, 'job:', jobId);
		return this.http.post<ITicketProcessBoardScanTicketsResponse>(
			INGEST_BOARDSCAN_TICKETS_ROUTE(projectId, jobId),
			null,
			{
				context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
					forceRefresh: true,
				}),
			}
		);
	}

	public expressImport(
		importTicketsData: ExpressImportTicketsData
	): Observable<ITicketImportResponseMetadata> {
		console.log('ExpressImport called with data:', importTicketsData);
		return this.http.post<ITicketImportResponseMetadata>(IMPORT_BOARDSCAN_TICKETS_ROUTE, importTicketsData, {
			context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
				forceRefresh: true,
			}),
		});
	}

	/**
	 * Gets the list of data sources for a project.
	 *
	 * @param projectId - The id of the project to get the data sources for.
	 * @returns An Observable emitting the list of data sources for the project.
	 */
	public getDataSourcesList(projectId: string): Observable<Map<string, IExternalSystem>> {
		return this.getProjectDataSourcesFirebaseObject(projectId)
			.valueChanges()
			.pipe(
				map((externalSystemList) => {
					if (!externalSystemList) {
						return new Map<string, IExternalSystem>();
					}
					const list = new Map<string, IExternalSystem>();
					for (const [key, value] of Object.entries(externalSystemList)) {
						list.set(key, value);
					}
					return list;
				})
			);
	}

	/**
	 * Gets the Project Data Sources from Firebase.
	 * @param projectId
	 */
	private getProjectDataSourcesFirebaseObject(projectId: string): AngularFireObject<IExternalSystemList> {
		const path = fancyFirebaseGetters.getProjectExternalSystemsPath(projectId);
		const ref = fancyFirebaseReferencer.getRef(path, projectId);
		return this.fireDB.object<IExternalSystemList>(fancyDatabase.toFirebaseRef(ref));
	}

	// re: array vs map
	// - map is better for organizing by ticketId, but has to be array'd anyways for display in the ui
	// - array can be used directly, but needs to manually manage the id, start with array mostly
	// because it's the default
	public getProcessingTickets(projectId: string, jobId: string) {
		const path = fancyFirebaseGetters.getProcessingTicketsJobPath(projectId, jobId);
		const ref = fancyFirebaseReferencer.getRef(path, projectId);
		return this.fireDB
			.list<ITPTicket<IBoardscanTicketMetadata>>(fancyDatabase.toFirebaseRef(ref))
			.snapshotChanges()
			.pipe(
				map((actions) => {
					return actions.map((action) => {
						const data = action.payload.val() as ITPTicket<IBoardscanTicketMetadata>;
						const key = action.payload.key;
						data.ticketId = key; // Ensure ticketId is set from the key
						return data;
					});
				})
			);
	}

	/** set the whole list enmass, replacing everything already there */
	public setProcessingTickets(
		projectId: string,
		jobId: string,
		tickets: ITPTicket<IBoardscanTicketMetadata>[],
		skip: ITPTicket[]
	) {
		const path = fancyFirebaseGetters.getProcessingTicketsJobPath(projectId, jobId);
		const ref = fancyFirebaseReferencer.getRef(path, projectId);

		const writableList = tickets.reduce((acc, ticket) => {
			acc[ticket.ticketId] = ticket;
			return acc;
		}, {} as Record<string, ITPTicket<IBoardscanTicketMetadata>>);

		skip.forEach((ticket) => {
			writableList[ticket.ticketId] = null;
		});

		return fancyDatabase.set(ref, writableList);
	}
}
